import React, { useState, useEffect } from 'react';
import './header.css';
import './header_internal.css';
import { useAuth } from '../services/AuthContext.js';
import { Notifications } from './notifications.js';
import {
  X, ChevronDown, LayoutDashboard, Library,
  MessageSquare, MessageCircle, UserCircle, LogOut, ArrowUpCircle, Users,
  Newspaper, Shield
} from 'lucide-react';
import { isAllowedAdmin } from '../config/admin';

const baseAPI_URL = process.env.REACT_APP_BACKEND_API_BASE_ENDPOINT;

function getNomeCurto(user) {
  const nomeCompleto = user.attributes?.name || user.getUsername();
  const primeiroNome = nomeCompleto.split(' ')[0];
  return primeiroNome.length > 12 ? primeiroNome.slice(0, 12) + '…' : primeiroNome;
}

const internalLinks = [
  { label: 'Dashboard', href: '/dashboard', icon: <LayoutDashboard size={18} /> },
  { label: 'Cursos', href: '/allcourses', icon: <Library size={18} /> },
  { label: 'News', href: '/news', icon: <MessageSquare size={18} /> },
  { label: 'Feed', href: '/feed', icon: <Newspaper size={18} /> },
  { label: 'Rede', href: '/network', icon: <Users size={18} /> },
  { label: 'Chats', href: '/chats', icon: <MessageCircle size={18} /> },
  { label: 'Sua conta', href: '/account', icon: <UserCircle size={18} /> },
];

export function HeaderInternal({ isMobile }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [accountType, setAccountType] = useState('unknown');
  const [showAdminLink, setShowAdminLink] = useState(false);
  const { user, getIdToken } = useAuth();

  const toggleMenu = () => setMenuOpen(prev => !prev);
  const navLinks = showAdminLink
    ? [...internalLinks, { label: 'Admin', href: '/admin/stats', icon: <Shield size={18} /> }]
    : internalLinks;

  const fetchAccountType = async () => {
    if (!user) {
      setShowAdminLink(false);
      return;
    }
    try {
      const token = await getIdToken();
      setShowAdminLink(isAllowedAdmin(user, token));
      const res = await fetch(`${baseAPI_URL}/users/get-user-data`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      const data = await res.json();
      setAccountType(data.account_type);
    } catch (err) {
      console.error("Failed to fetch user data", err);
    }
  };

  useEffect(() => {
    fetchAccountType();
  }, [user]);

  useEffect(() => {
    const header = document.querySelector('.header-internal');
    if (header) {
      if (menuOpen) header.classList.add('fixed-top');
      else header.classList.remove('fixed-top');
    }
  }, [menuOpen]);

  let authLinks = [];

  if (user && accountType === 'free') {
    authLinks = [
      {
        label: `${getNomeCurto(user)} (sair)`,
        href: '/logon',
        type: 'logout',
        icon: <LogOut size={18} />,
      },
      {
        label: 'Premium',
        href: '/premium',
        type: 'upgrade',
        icon: <ArrowUpCircle size={18} />,
      },
    ];
  } else if (user) {
    authLinks = [
      {
        label: `${getNomeCurto(user)} (sair)`,
        href: '/logon',
        type: 'logout',
        icon: <LogOut size={18} />,
      },
    ];
  }

  if (isMobile) {
    return (
      <header className="header header-internal">
        <div className="logo-container">
          <a href="/dashboard" title="Domínio Elétrico" className="logo-link">
            <img
              src="/dominio_eletrico_logo_2023_square_fundo_transparente.png"
              alt="Logo do Domínio Elétrico"
              className="logo"
            />
          </a>
        </div>

        <nav className="nav">
          <Notifications />
          <button onClick={toggleMenu} className="menu-button" aria-expanded={menuOpen}>
            <span className="menu-label">
              Menu {menuOpen ? <X size={22} /> : <ChevronDown size={22} />}
            </span>
          </button>

          {menuOpen && (
            <ul className="dropdown" role="menu">
              <div className="mobile-auth-buttons">
                {authLinks.map(link => (
                  <a
                    key={link.href + link.type}
                    href={link.href}
                    className={`auth-button mobile-auth-button ${link.type}`}
                  >
                    {link.icon} {link.label}
                  </a>
                ))}
              </div>
              {navLinks.map(link => (
                <li key={link.href}>
                  <a href={link.href} className="link internal-link" role="menuitem">
                    {link.icon} {link.label}
                  </a>
                </li>
              ))}
            </ul>
          )}
        </nav>
      </header>
    );
  }

  return (
    <header className="header header-desktop header-internal">
      <div className="logo-container">
        <a href="/dashboard" title="Domínio Elétrico" className="logo-link">
          <img
            src="/dominio_eletrico_logo_2023_square_fundo_transparente.png"
            alt="Logo do Domínio Elétrico"
            className="logo"
          />
        </a>
      </div>

      <nav className="nav public-nav header-internal-nav">
        {navLinks.map(link => (
          <a key={link.href} href={link.href} className="menu-button-desktop internal-link">
            {link.icon} {link.label}
          </a>
        ))}
      </nav>

      <nav className="nav internal-nav">
        <Notifications />

        {authLinks.map(link => (
          <a
            key={link.href + link.type}
            href={link.href}
            className={`menu-button-desktop auth-button ${link.type}`}
          >
            {link.icon} {link.label}
          </a>
        ))}
      </nav>
    </header>
  );
}
